<script setup lang="ts">
import { AppRawButton, AppSvgIcon, AppTooltip } from '@libs/ui';

import type { CurrentMonthUnavailableDayTile } from '../models';
import { TileSelectionType } from '../models';

interface CurrentMonthUnavailableDayTileViewProps {
  calendarTile: CurrentMonthUnavailableDayTile;
  showPrices: boolean;
  allowSelection: boolean;
}

interface CurrentMonthUnavailableDayTileViewEmits {
  (eventName: 'select-date', date: Date): void;
}

const emit = defineEmits<CurrentMonthUnavailableDayTileViewEmits>();

const props = defineProps<CurrentMonthUnavailableDayTileViewProps>();

function onClick(): void {
  if (props.allowSelection) {
    emit('select-date', props.calendarTile.date);
  }
}
</script>

<template>
  <div class="current-month-unavailable-day-tile-view-wrapper">
    <AppRawButton
      class="current-month-unavailable-day-tile-view"
      :class="{
        selected: props.calendarTile.selection === TileSelectionType.Selected,
        'allow-hover': props.allowSelection,
      }"
      @click="onClick"
    >
      {{ props.calendarTile.text }}
      <div v-if="props.showPrices" class="price-placeholder">-</div>
    </AppRawButton>
    <AppTooltip
      v-if="props.showPrices"
      position="top-left"
      class="info-tooltip"
      :ariaLabel="props.calendarTile.reason"
    >
      <template #trigger>
        <AppSvgIcon name="infoCircle" size="16" />
      </template>
      <template #default>{{ props.calendarTile.reason }}</template>
    </AppTooltip>
  </div>
</template>

<style scoped lang="scss">
@use '../styles/price_calendar_mixins' as *;

.current-month-unavailable-day-tile-view-wrapper {
  position: relative;

  .current-month-unavailable-day-tile-view {
    width: var(--calendar-tile-size);
    height: var(--calendar-tile-size);
    border-radius: var(--calendar-tile-border-radius);
    background-color: var(--card-tile-background-color);
    transition: background-color var(--transition-short);
    @include calendar-tile-day-font;
    color: var(--calendar-tile-day-font-color);
    cursor: default;

    display: flex;
    flex-direction: column;
    gap: var(--calendar-tile-day-price-gap);
    align-items: center;
    justify-content: center;

    position: relative;

    &.allow-hover {
      @include on-hover {
        background-color: var(--calendar-tile-hover-color);
        cursor: pointer;
      }
    }

    &.selected {
      background-color: var(--color-neutral-70);
      color: var(--color-white);

      @include on-hover {
        background-color: var(--color-neutral-70);
      }

      .info-tooltip {
        color: var(--color-white);
      }
    }

    .price-placeholder {
      height: var(--calendar-price-placeholder-height);
    }
  }
  .info-tooltip {
    position: absolute;
    padding: 0;
    right: var(--calendar-tile-info-icon-right);
    top: var(--calendar-tile-info-icon-top);
    color: var(--calendar-tile-day-font-color);

    @include for-less-or-equal-than-599-px {
      right: var(--calendar-tile-info-icon-right-mobile);
    }
  }
}
</style>
